import { FC, useEffect } from "react";
import { observer } from "mobx-react";
import { useDispatch, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { AccountCompanyDetails } from "@/common/common";
import { Alert } from "@/components-v2/Alert";
import { Box, VBox } from "@/components-v2/Box";
import { Button, LinkButton } from "@/components-v2/Button";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { Dialog } from "@/components-v2/Dialog";
import { EmptyState } from "@/components-v2/EmptyState";
import { Icon } from "@/components-v2/Icon";
import { MCheckboxGroup } from "@/components-v2/Checkbox";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { Tabs } from "@/components-v2/Tabs";
import { Tag } from "@/components-v2/Tag";
import { MTextArea } from "@/components-v2/TextArea";
import { MTextInput } from "@/components-v2/TextInput";
import { EventDispatcher } from "@/events/eventsDomain";
import {
  AccountCompanyRolesState,
  AccountRole,
  AccountRoleTypeDialog,
  accountRoleTypeActivationError,
  accountRoleTypeActivationIntro,
  accountRoleTypeDeleteMessage,
  accountRolesDescription,
  accountRolesFor,
  accountRoleSubtitle,
  accountRoleSystemAccessLabel,
  initAccountCompanyRoles,
  prepAccountRoleActions,
  prepAccountRoleActivationTag,
  prepAccountRoleFilterTabs,
  prepAccountRolesEmptyState,
  prepAccountRolesSearch,
  prepAccountRoleTypeDescriptionInput,
  prepAccountRoleTypeDialog,
  prepAccountRoleTypeNameInput,
  prepAccountRoleTypeTabs,
  prepAddAccountRoleButton,
  prepAddDescriptionButton,
  prepRemoveDescriptionButton,
  RoleTypeFormDialog,
} from "./accountRolesDomain";

type AccountRolesViewProps = {
  state: AccountCompanyRolesState;
  dispatch: EventDispatcher;
};

export const AccountRolesSection: FC<{
  accountId: string;
  accountName: string;
  companies: AccountCompanyDetails[];
}> = observer(({ accountId, accountName, companies }) => {
  const rootStore = useRootStore();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      initAccountCompanyRoles(
        accountId,
        accountName,
        companies.map((company) => ({ companyId: company.companyId, name: company.companyName })),
      ),
    );
  }, [dispatch, accountId, accountName, companies]);
  const state = rootStore.accountCompanyRoles;
  return state?.accountId === accountId ? (
    <AccountRolesView state={state} dispatch={dispatch} />
  ) : null;
});

const AccountRolesView: FC<AccountRolesViewProps> = observer(({ state, dispatch }) => {
  const { t } = useTranslation();
  return (
    <VBox fullheight alignItems={"stretch"}>
      <VBox bg={"default"} borderBottom alignItems={"stretch"}>
        <VBox container maxWidth={"lg"} paddingX={"lg"} paddingTop={"lg"} alignItems={"stretch"}>
          <Box justifyContent={"space-between"} alignItems={"start"} gap={"md"}>
            <VBox gap={"xs"}>
              <h3>{t("enterprise-account.company-roles")}</h3>
              <div>{t(accountRolesDescription(state))}</div>
            </VBox>
            <Button {...prepAddAccountRoleButton(state)} />
          </Box>
          <Tabs tabs={prepAccountRoleFilterTabs(state, dispatch)} />
        </VBox>
      </VBox>
      <VBox container padding={"lg"} gap={"lg"} maxWidth={"lg"} alignItems={"stretch"} grow>
        <Box>
          <MTextInput {...prepAccountRolesSearch(state)} />
        </Box>
        <AccountRolesTable state={state} />
      </VBox>
      {state.dialog && <AccountRoleTypeDialogView state={state} dialog={state.dialog} />}
    </VBox>
  );
});

const AccountRoleTypeDialogView: FC<{
  state: AccountCompanyRolesState;
  dialog: AccountRoleTypeDialog;
}> = observer(({ state, dialog }) => {
  const { t } = useTranslation();
  return (
    <Dialog {...prepAccountRoleTypeDialog(state, dialog)}>
      {dialog.type === "delete" ? (
        <div>{t(accountRoleTypeDeleteMessage(dialog))}</div>
      ) : (
        <VBox gap={"lg"}>
          <Tabs tabs={prepAccountRoleTypeTabs(state, dialog)} borderBottom />
          <AccountRoleTypeTab state={state} dialog={dialog} />
        </VBox>
      )}
    </Dialog>
  );
});

const AccountRoleTypeTab: FC<{
  state: AccountCompanyRolesState;
  dialog: RoleTypeFormDialog;
}> = observer(({ state, dialog }) => {
  const { t } = useTranslation();
  switch (dialog.tab) {
    case "setup":
      return (
        <VBox gap={"lg"}>
          <MTextInput {...prepAccountRoleTypeNameInput(dialog)} />
          {dialog.showDescription ? (
            <MTextArea
              {...prepAccountRoleTypeDescriptionInput(dialog)}
              labelAction={<LinkButton {...prepRemoveDescriptionButton(state)} />}
            />
          ) : (
            <Box>
              <LinkButton {...prepAddDescriptionButton(state)} />
            </Box>
          )}
        </VBox>
      );
    case "permissions":
      return (
        <VBox gap={"md"}>
          <div>{t("enterprise-account.company-roles.permissions-intro")}</div>
          <Box className={"font-semibold"}>
            {t("enterprise-account.company-roles.system-access")}
          </Box>
          <MRadioButtonGroup model={dialog.systemAccess} horizontal />
        </VBox>
      );
    case "activation":
      return (
        <VBox gap={"md"}>
          <div>{t(accountRoleTypeActivationIntro(state))}</div>
          <Box className={"font-semibold"}>
            {t("enterprise-account.company-roles.activate-for-companies")}
          </Box>
          <MRadioButtonGroup model={dialog.activation} horizontal />
          {dialog.activation.value === "SELECTED_COMPANIES" && (
            <VBox gap={"md"}>
              <Box gap={"xs"}>
                <LinkButton
                  label={{ key: "select-all" }}
                  onClick={dialog.companySelection.selectAll}
                />
                <span>/</span>
                <LinkButton label={{ key: "none" }} onClick={dialog.companySelection.clear} />
              </Box>
              <Box overflowY={"auto"} className={"max-h-80"}>
                <MCheckboxGroup model={dialog.companySelection} />
              </Box>
            </VBox>
          )}
          <ActivationError dialog={dialog} />
        </VBox>
      );
  }
});

const ActivationError: FC<{ dialog: RoleTypeFormDialog }> = observer(({ dialog }) => {
  const { t } = useTranslation();
  const error = accountRoleTypeActivationError(dialog);
  return error ? <Alert type={"error"} message={t(error)} /> : null;
});

const AccountRolesTable: FC<{ state: AccountCompanyRolesState }> = observer(({ state }) => {
  const roles = accountRolesFor(state, state.filter);
  const tableColumns: TableColumn<AccountRole>[] = [
    {
      name: "title",
      align: "left",
      label: { key: "enterprise-account.company-roles.title" },
      getFilterValue: (row) => row.title,
      getSortValue: (row, _, { t }) => t(row.title),
      getValue: (row, _, { t }) => (
        <Box alignItems={"center"} gap={"md"}>
          <Icon name={"user-circle"} size={"lg"} />
          <VBox>
            <div>{t(row.title)}</div>
            <Box fg={"subtle"} textSize={"sm"}>
              {t(accountRoleSubtitle(state, row))}
            </Box>
          </VBox>
        </Box>
      ),
    },
    {
      name: "activations",
      align: "left",
      label: { key: "enterprise-account.company-roles.activations" },
      notSorted: true,
      getValue: (row) => <Tag {...prepAccountRoleActivationTag(row)} />,
    },
    {
      name: "systemAccess",
      align: "left",
      label: { key: "enterprise-account.company-roles.system-access" },
      getValue: (row) => accountRoleSystemAccessLabel(row),
    },
  ];
  return roles.length === 0 ? (
    <EmptyState {...prepAccountRolesEmptyState(state)} />
  ) : (
    <DataTable
      tableColumns={tableColumns}
      tableData={roles}
      getId={(row) => row.id}
      filter={state.search.inputValue}
      rowActions={(row) => prepAccountRoleActions(state, row)}
    />
  );
});
